import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logAuditAction } from '@/hooks/useAuditLog';

export interface WorkshopInfo {
  problema_concreto: string | null;
  resultado_esperado: string | null;
  tiempo_estimado: string | null;
  tip_adicional: string | null;
  title: string | null;
  deleted_at: string | null;
}

export interface DeliverableRow {
  id: string;
  user_id: string;
  name: string | null;
  description: string | null;
  status: string | null;
  solution_type: number | null;
  tool_used: string | null;
  file_url: string | null;
  file_urls: string[] | null;
  comment: string | null;
  score: number | null;
  feedback: string | null;
  submitted_at: string | null;
  validated_at: string | null;
  validated_by: string | null;
  workshop_id: string | null;
  cohort_id: string | null;
  workshop: WorkshopInfo | null;
}

export function useDeliverables() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['deliverables', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('participant_deliverables')
        .select('*, workshop:program_workshops(problema_concreto, resultado_esperado, tiempo_estimado, tip_adicional, title, deleted_at)')
        .eq('user_id', user.id)
        .order('submitted_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as DeliverableRow[];
    },
    enabled: !!user,
  });
}

export function useCreateDeliverable() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deliverable: {
      name: string;
      description?: string;
      solution_type?: number;
      tool_used?: string | null;
      file_url?: string;
      file_urls?: string[];
      comment?: string;
      workshop_id?: string;
      cohort_id?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('participant_deliverables')
        .insert({
          ...deliverable,
          user_id: user.id,
          status: 'submitted' as const,
          submitted_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      queryClient.invalidateQueries({ queryKey: ['weekly-progress'] });
      if (user) {
        logAuditAction(user.id, 'deliverable_submit', `Entregable enviado: ${data.name ?? 'Sin nombre'}`, { deliverable_id: data.id } as any);
      }
    },
  });
}
